// /admin/continuations — admin review queue for crowdfund bids and
// volunteer commits. Approve / deny / activate / cancel each. Also marks
// individual pledges 'paid' (manual until Stripe lands).

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AdminNav } from '../components/AdminNav';

interface CrowdfundRow {
  id: number;
  status: string;
  resident_id: number;
  resident_name: string;
  resident_address: string;
  resident_city: string;
  cycle_end: string | null;
  continuation_opt_in: string;
  bid_amount_cents: number;
  cycle_months: number;
  bid_notes: string | null;
  admin_notes: string | null;
  mowkin_label: string | null;
  mowkin_kind: 'volunteer' | 'provider' | null;
  pledged_cents: number;
  created_at: string;
}
interface CommitRow {
  id: number;
  status: string;
  resident_id: number;
  resident_name: string;
  resident_address: string;
  resident_city: string;
  continuation_opt_in: string;
  volunteer_id: number;
  volunteer_name: string;
  cadence: string;
  custom_cadence: string | null;
  preferred_day: string | null;
  start_date: string;
  end_date: string | null;
  notes: string | null;
  admin_notes: string | null;
  created_at: string;
}

function fmtMoney(cents: number) {
  return `$${(cents / 100).toFixed(2)}`;
}

function StatusPill({ status }: { status: string }) {
  const map: Record<string, string> = {
    proposed: 'bg-sand text-forest',
    approved: 'bg-forest text-cream',
    denied:   'bg-rose/20 text-rose',
    active:   'bg-forest text-cream',
    funded:   'bg-forest text-cream',
    completed:'bg-forest text-cream',
    cancelled:'bg-forest/10 text-forest/60',
    paused:   'bg-sand text-forest',
  };
  return (
    <span className={`inline-block px-2 py-0.5 text-[10px] tracking-[0.15em] uppercase ${map[status] || 'bg-forest/10 text-forest'}`}>
      {status}
    </span>
  );
}

export function AdminContinuationsPage() {
  const [crowdfunds, setCrowdfunds] = useState<CrowdfundRow[]>([]);
  const [commits, setCommits] = useState<CommitRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [tab, setTab] = useState<'crowdfunds' | 'commits'>('crowdfunds');

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const res = await fetch('/api/admin/continuations', { credentials: 'include' });
      const j = await res.json() as {
        ok: boolean;
        data?: { crowdfunds: CrowdfundRow[]; commits: CommitRow[] };
        error?: string;
      };
      if (!j.ok) throw new Error(j.error || 'fetch failed');
      setCrowdfunds(j.data?.crowdfunds || []);
      setCommits(j.data?.commits || []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Load failed');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { void load(); }, []);

  const act = async (kind: 'crowdfund' | 'commit', id: number, action: string, notes?: string) => {
    const key = `${kind}-${id}-${action}`;
    setBusyKey(key);
    try {
      const res = await fetch('/api/admin/continuations', {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kind, id, action, notes }),
      });
      const j = await res.json() as { ok: boolean; error?: string };
      if (!j.ok) throw new Error(j.error || 'Action failed');
      void load();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Action failed');
    } finally {
      setBusyKey(null);
    }
  };

  const pendingCount = {
    crowdfunds: crowdfunds.filter((c) => c.status === 'proposed').length,
    commits:    commits.filter((c) => c.status === 'proposed').length,
  };

  return (
    <>
      <AdminNav />
      <div className="max-w-6xl mx-auto px-6 lg:px-12 py-12">
        <div className="text-xs tracking-[0.25em] uppercase text-rose mb-3">Admin · Continuations</div>
        <h1 className="text-4xl text-forest mb-2 font-display" style={{ fontVariationSettings: '"opsz" 144' }}>
          Continuation queue.
        </h1>
        <p className="text-forest/70 mb-6">
          Review Mowkin bids and volunteer commitments for households whose
          free cycle has ended.{' '}
          <Link to="/lifecycle" className="text-rose hover:underline">How this works →</Link>
        </p>

        {error && <div className="bg-rose/10 border-l-4 border-rose p-4 text-rose text-sm mb-6">{error}</div>}

        <div className="flex gap-1 border-b border-forest/10 mb-6">
          <button
            onClick={() => setTab('crowdfunds')}
            className={`px-5 py-3 text-sm tracking-[0.1em] uppercase transition-colors ${tab === 'crowdfunds' ? 'bg-forest text-cream' : 'text-forest/70 hover:text-forest hover:bg-forest/5'}`}
          >
            Crowdfund bids ({crowdfunds.length}{pendingCount.crowdfunds > 0 ? ` · ${pendingCount.crowdfunds} pending` : ''})
          </button>
          <button
            onClick={() => setTab('commits')}
            className={`px-5 py-3 text-sm tracking-[0.1em] uppercase transition-colors ${tab === 'commits' ? 'bg-forest text-cream' : 'text-forest/70 hover:text-forest hover:bg-forest/5'}`}
          >
            Commits ({commits.length}{pendingCount.commits > 0 ? ` · ${pendingCount.commits} pending` : ''})
          </button>
        </div>

        {loading ? (
          <div className="text-forest/60 italic">Loading...</div>
        ) : tab === 'crowdfunds' ? (
          <div className="space-y-3">
            {crowdfunds.length === 0 && <p className="text-forest/55 italic">No crowdfund bids yet.</p>}
            {crowdfunds.map((c) => {
              const target = c.bid_amount_cents * c.cycle_months;
              const pct = target > 0 ? Math.min(100, Math.round((c.pledged_cents / target) * 100)) : 0;
              return (
                <div key={c.id} className="bg-cream border border-forest/15 p-5">
                  <div className="flex items-center gap-3 flex-wrap mb-2">
                    <StatusPill status={c.status} />
                    <span className="text-xs text-forest/55">#{c.id} · {new Date(c.created_at).toLocaleDateString()}</span>
                    <span className="text-[10px] tracking-[0.15em] uppercase bg-forest/5 text-forest/70 px-2 py-0.5">
                      opt-in: {c.continuation_opt_in}
                    </span>
                  </div>
                  <div className="text-forest font-medium">{c.resident_name}</div>
                  <div className="text-sm text-forest/65 mb-2">{c.resident_address}, {c.resident_city}</div>
                  <Link to={`/admin/photo-vetting/${c.resident_id}`} className="text-xs tracking-[0.15em] uppercase text-rose hover:underline mb-2 inline-block">Vet photos →</Link>
                  <div className="text-sm text-forest/80 mb-1">
                    Mowkin: <strong>{c.mowkin_label}</strong>
                    {c.mowkin_kind && <span className="text-forest/55 text-xs ml-1">({c.mowkin_kind})</span>}
                  </div>
                  <div className="text-sm text-forest/80 mb-2">
                    Bid: <strong>{fmtMoney(c.bid_amount_cents)}</strong>/month for{' '}
                    <strong>{c.cycle_months}</strong> month{c.cycle_months === 1 ? '' : 's'} ·{' '}
                    Total target: <strong>{fmtMoney(target)}</strong>
                  </div>
                  {(c.status === 'approved' || c.status === 'active' || c.status === 'funded') && (
                    <div className="mb-2">
                      <div className="bg-forest/5 h-2 mb-1 overflow-hidden">
                        <div className="bg-rose h-full" style={{ width: `${pct}%` }} />
                      </div>
                      <div className="text-xs text-forest/55">
                        {fmtMoney(c.pledged_cents)} pledged ({pct}%)
                      </div>
                    </div>
                  )}
                  {c.bid_notes && <div className="text-xs italic text-forest/65 my-2 border-l-2 border-forest/20 pl-3">Bid notes: {c.bid_notes}</div>}
                  {c.admin_notes && <div className="text-xs italic text-forest/65 my-2 border-l-2 border-forest/20 pl-3">Admin notes: {c.admin_notes}</div>}

                  <div className="flex flex-wrap gap-2 mt-3">
                    {c.status === 'proposed' && (
                      <>
                        <button disabled={busyKey?.startsWith(`crowdfund-${c.id}`)} onClick={() => act('crowdfund', c.id, 'approve')} className="text-xs tracking-[0.1em] uppercase bg-forest text-cream px-4 py-2 hover:bg-rose transition-colors disabled:opacity-50">Approve</button>
                        <button disabled={busyKey?.startsWith(`crowdfund-${c.id}`)} onClick={() => act('crowdfund', c.id, 'deny')} className="text-xs tracking-[0.1em] uppercase bg-rose/20 text-rose px-4 py-2 hover:bg-rose hover:text-cream transition-colors disabled:opacity-50">Deny</button>
                      </>
                    )}
                    {c.status === 'approved' && (
                      <button disabled={busyKey?.startsWith(`crowdfund-${c.id}`)} onClick={() => act('crowdfund', c.id, 'activate')} className="text-xs tracking-[0.1em] uppercase bg-forest text-cream px-4 py-2 hover:bg-rose transition-colors disabled:opacity-50">Activate</button>
                    )}
                    {(c.status === 'active' || c.status === 'approved') && pct >= 100 && (
                      <button disabled={busyKey?.startsWith(`crowdfund-${c.id}`)} onClick={() => act('crowdfund', c.id, 'mark_funded')} className="text-xs tracking-[0.1em] uppercase bg-forest text-cream px-4 py-2 hover:bg-rose transition-colors disabled:opacity-50">Mark funded</button>
                    )}
                    {c.status === 'funded' && (
                      <button disabled={busyKey?.startsWith(`crowdfund-${c.id}`)} onClick={() => act('crowdfund', c.id, 'complete')} className="text-xs tracking-[0.1em] uppercase bg-forest text-cream px-4 py-2 hover:bg-rose transition-colors disabled:opacity-50">Mark complete</button>
                    )}
                    {!['cancelled', 'completed', 'denied'].includes(c.status) && (
                      <button disabled={busyKey?.startsWith(`crowdfund-${c.id}`)} onClick={() => act('crowdfund', c.id, 'cancel')} className="text-xs tracking-[0.1em] uppercase bg-forest/10 text-forest px-4 py-2 hover:bg-forest hover:text-cream transition-colors disabled:opacity-50">Cancel</button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="space-y-3">
            {commits.length === 0 && <p className="text-forest/55 italic">No commits yet.</p>}
            {commits.map((c) => (
              <div key={c.id} className="bg-cream border border-forest/15 p-5">
                <div className="flex items-center gap-3 flex-wrap mb-2">
                  <StatusPill status={c.status} />
                  <span className="text-xs text-forest/55">#{c.id} · {new Date(c.created_at).toLocaleDateString()}</span>
                  <span className="text-[10px] tracking-[0.15em] uppercase bg-forest/5 text-forest/70 px-2 py-0.5">
                    opt-in: {c.continuation_opt_in}
                  </span>
                </div>
                <div className="text-forest font-medium">{c.volunteer_name} → {c.resident_name}</div>
                <div className="text-sm text-forest/65 mb-2">{c.resident_address}, {c.resident_city}</div>
                <div className="text-sm text-forest/80 mb-2">
                  Cadence: <strong>{c.cadence === 'custom' ? (c.custom_cadence || 'custom') : c.cadence}</strong>
                  {c.preferred_day && <span> · {c.preferred_day}s</span>}
                  {' · '} {c.start_date}{c.end_date ? ` → ${c.end_date}` : ' → open-ended'}
                </div>
                {c.notes       && <div className="text-xs italic text-forest/65 my-2 border-l-2 border-forest/20 pl-3">Volunteer: {c.notes}</div>}
                {c.admin_notes && <div className="text-xs italic text-forest/65 my-2 border-l-2 border-forest/20 pl-3">Admin: {c.admin_notes}</div>}

                <div className="flex flex-wrap gap-2 mt-3">
                  {c.status === 'proposed' && (
                    <>
                      <button disabled={busyKey?.startsWith(`commit-${c.id}`)} onClick={() => act('commit', c.id, 'approve')} className="text-xs tracking-[0.1em] uppercase bg-forest text-cream px-4 py-2 hover:bg-rose transition-colors disabled:opacity-50">Approve</button>
                      <button disabled={busyKey?.startsWith(`commit-${c.id}`)} onClick={() => act('commit', c.id, 'deny')} className="text-xs tracking-[0.1em] uppercase bg-rose/20 text-rose px-4 py-2 hover:bg-rose hover:text-cream transition-colors disabled:opacity-50">Deny</button>
                    </>
                  )}
                  {c.status === 'approved' && (
                    <button disabled={busyKey?.startsWith(`commit-${c.id}`)} onClick={() => act('commit', c.id, 'activate')} className="text-xs tracking-[0.1em] uppercase bg-forest text-cream px-4 py-2 hover:bg-rose transition-colors disabled:opacity-50">Activate</button>
                  )}
                  {c.status === 'active' && (
                    <>
                      <button disabled={busyKey?.startsWith(`commit-${c.id}`)} onClick={() => act('commit', c.id, 'pause')} className="text-xs tracking-[0.1em] uppercase bg-sand text-forest px-4 py-2 hover:bg-forest hover:text-cream transition-colors disabled:opacity-50">Pause</button>
                      <button disabled={busyKey?.startsWith(`commit-${c.id}`)} onClick={() => act('commit', c.id, 'complete')} className="text-xs tracking-[0.1em] uppercase bg-forest text-cream px-4 py-2 hover:bg-rose transition-colors disabled:opacity-50">Mark complete</button>
                    </>
                  )}
                  {!['cancelled', 'completed', 'denied'].includes(c.status) && (
                    <button disabled={busyKey?.startsWith(`commit-${c.id}`)} onClick={() => act('commit', c.id, 'cancel')} className="text-xs tracking-[0.1em] uppercase bg-forest/10 text-forest px-4 py-2 hover:bg-forest hover:text-cream transition-colors disabled:opacity-50">Cancel</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
